import { Button, IconButton, Typography } from "@povio/ui";
import Lottie, { type LottieRefCurrentProps } from "lottie-react";
import type { CardComponentProps } from "nextstepjs";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { ChevronLeftIcon } from "@/assets/icons/general/ChevronLeft";
import { ChevronRightIcon } from "@/assets/icons/general/ChevronRight";
import { useDynamicTourContent } from "@/hooks/useDynamicTourContent";
import AdogExitAnimation from "@/lottie/AdogExit.json";
import AdogIntroAnimation from "@/lottie/AdogIntro.json";
import AdogTalkingAnimation from "@/lottie/AdogTalking.json";

type AnimationPhase = "intro" | "talking" | "exit";

export const MobileTourCard = ({ step, currentStep, totalSteps, nextStep, prevStep }: CardComponentProps) => {
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;
  const lottieRef = useRef<LottieRefCurrentProps>(null);

  // State for tracking animation phase and completing state
  const [animationPhase, setAnimationPhase] = useState<AnimationPhase>(isFirstStep ? "intro" : "talking");
  const [isCompleting, setIsCompleting] = useState(false);

  // Track if intro animation has already been shown (to prevent replay on navigation back)
  const hasShownIntro = useRef(false);

  // Reset to intro animation when returning to first step
  useEffect(() => {
    if (isFirstStep && !isCompleting) {
      setAnimationPhase("intro");
    }
  }, [isFirstStep, isCompleting]);

  // Set animation direction based on phase
  useEffect(() => {
    if (lottieRef.current) {
      if (animationPhase === "intro") {
        // Play in reverse for intro (scale up effect)
        lottieRef.current.setDirection(-1);
      } else {
        // Play forward for talking and exit
        lottieRef.current.setDirection(1);
      }
    }
  }, [animationPhase]);

  const dynamicContent = useDynamicTourContent(step);

  // For SSR safety, check if document is available
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!step || !isMounted) return null;

  // Handle intro animation completion - transition to talking
  const handleIntroComplete = () => {
    if (animationPhase === "intro") {
      hasShownIntro.current = true;
      setAnimationPhase("talking");
    }
  };

  // Handle exit animation completion - complete the tour
  const handleExitComplete = () => {
    if (isCompleting && nextStep) {
      // Completing the tour - advance to next step (which will mark tour as complete)
      nextStep();
    }
  };

  // Handle complete button click - start exit animation then complete tour
  const handleComplete = () => {
    setIsCompleting(true);
    setAnimationPhase("exit");
  };

  // Get current animation data
  const getCurrentAnimation = () => {
    switch (animationPhase) {
      case "intro":
        return AdogIntroAnimation;
      case "talking":
        return AdogTalkingAnimation;
      case "exit":
        return AdogExitAnimation;
      default:
        return AdogTalkingAnimation;
    }
  };

  // Use portal to escape the transform context from NextStepJS parent elements
  // This is necessary because the parent has transform applied which creates a new
  // containing block that breaks position: fixed
  return createPortal(
    <div
      className="fixed right-0 bottom-0 left-0 z-9999"
      data-tour-card="true"
      data-tour-card-mobile="true"
    >
      <div className="relative flex flex-col gap-24 rounded-t-xl bg-elevation-fill-default-2 px-6 pt-6 shadow-[0_10px_15px_-3px_rgba(0,0,0,0.15),0_4px_6px_-2px_rgba(0,0,0,0.09),0_-8px_16px_-2px_rgba(0,0,0,0.25),0_-4px_8px_-1px_rgba(0,0,0,0.15)]">
        {/* Content */}
        <Typography
          size="body-4"
          className="text-text-default-2"
        >
          {dynamicContent}
        </Typography>

        {/* Controls */}
        <div className="flex items-center gap-4 pb-6">
          {/* Back Button */}
          {!isFirstStep && (
            <IconButton
              label="Back"
              size="s"
              color="secondary"
              disableTooltip
              className="bg-transparent"
              variant="outlined"
              icon={ChevronLeftIcon}
              onPress={prevStep}
            />
          )}

          <Button
            variant="outlined"
            color="secondary"
            size="s"
            iconPosition="right"
            icon={ChevronRightIcon}
            className="bg-transparent"
            onPress={isLastStep ? handleComplete : nextStep}
          >
            {isLastStep ? "Finish" : "Continue"}
          </Button>
        </div>

        {/* Dog Character - positioned in bottom right */}
        <div className="pointer-events-none absolute right-0 bottom-0 h-40 w-30 overflow-hidden">
          <Lottie
            lottieRef={lottieRef}
            animationData={getCurrentAnimation()}
            loop={animationPhase === "talking"}
            autoplay
            initialSegment={
              animationPhase === "intro" ? [getCurrentAnimation().op, getCurrentAnimation().ip] : undefined
            }
            onComplete={animationPhase === "intro" ? handleIntroComplete : handleExitComplete}
            className="h-[150px] w-[150px] origin-center translate-x-4 translate-y-4 scale-[180%] will-change-transform"
            style={{
              // Safari-specific optimizations for crisp scaling
              WebkitFontSmoothing: "antialiased",
              imageRendering: "-webkit-optimize-contrast",
            }}
            {...({
              // Safari rendering optimizations - use canvas with high DPI BEFORE scaling
              renderer: "canvas",
              rendererSettings: {
                clearCanvas: true,
                progressiveLoad: false,
                dpr: typeof window !== "undefined" ? (window.devicePixelRatio || 2) * 2 : 4,
              },
            } as any)}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
};
